(function () {
  const containers = document.querySelectorAll(
    '[class*="newsletter-signup--"]',
  );
  containers.forEach(init);

  function init(root) {
    const locale = root.dataset.shopLocale || "de";
    const errorText = root.dataset.errorText || "";
    const gatedContentApiUrl = `/apps/gated-download/newsletter?locale=${encodeURIComponent(locale)}`;

    /*
     * Snapshot from render time, not a live login state: a page restored from
     * the back/forward cache still carries the value from before the login.
     * Good enough to prefill the form; the server decides everything else.
     */
    const customerLoggedIn = root.dataset.customer === "1";
    if (customerLoggedIn) {
      checkNewsletterStatus();
    }

    window.addEventListener("pageshow", (event) => {
      if (event.persisted) checkNewsletterStatus();
    });

    const resendButton = root.querySelector("[data-resend-button]");
    if (resendButton) {
      resendButton.addEventListener("click", onResend);
    }

    root.querySelectorAll("[data-newsletter-form]").forEach((form) => {
      form.addEventListener("submit", (event) => onSubmit(event, form));
    });

    async function checkNewsletterStatus() {
      try {
        const res = await fetch(gatedContentApiUrl, {
          credentials: "same-origin",
        });
        const newsletterData = await res.json();
        if (newsletterData.state === "subscribed") {
          setState("subscribed");
        } else if (newsletterData.state === "pending") {
          setState("pending");
        } else if (newsletterData.state === "not_subscribed") {
          setState("not_subscribed");
        } else if (newsletterData.state === "error") {
          showError(newsletterData.reason || errorText);
        } else {
          setState("anonymous");
        }
      } catch (err) {
        console.error("[newsletter-signup] status error:", err);
        showError(errorText);
      }
    }

    async function onSubmit(event, form) {
      event.preventDefault();
      const data = new FormData(form);
      const email = String(data.get("email") || "").trim();
      const firstName = String(data.get("firstName") || "").trim();
      const consent =
        data.get("consent") === "on" || data.get("consent") === "true";
      if (!email) return;

      const button = form.querySelector("[data-submit-button]");
      const errorEl = form.querySelector("[data-error-message]");
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = "";
      }
      if (button) {
        button.disabled = true;
        button.textContent = button.dataset.loadingText || button.textContent;
      }

      try {
        const res = await fetch(gatedContentApiUrl, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            firstName: firstName || null,
            consent,
            source: "sewsimple-storefront",
          }),
        });
        const result = await res.json();

        if (!result.success) {
          if (errorEl) {
            errorEl.textContent = result.message || errorText;
            errorEl.hidden = false;
          }
          resetButton(button);
          return;
        }

        /*
         * `pending` beats `alreadySubscribed`: an address that is on the list but
         * unconfirmed must not be told "you are already subscribed" while the
         * gated download stays locked.
         */
        if (result.pending) {
          toggleResentNote(result.activationMailResent === true);
          setState("pending");
        } else if (result.alreadySubscribed) {
          setState(customerLoggedIn ? "subscribed" : "already_subscribed");
        } else {
          setState("success");
        }

        if (!result.pending && window.Shopify?.analytics?.publish) {
          Shopify.analytics.publish("webnetz_newsletter_signup", {
            newsletter_position: "section",
          });
        }

        // Lets the gated download button re-check without a page reload.
        document.dispatchEvent(
          new CustomEvent("webnetz:newsletter:subscribed", {
            detail: { pending: result.pending === true },
          }),
        );
      } catch (err) {
        console.error("[newsletter-signup] subscribe error:", err);
        if (errorEl) {
          errorEl.textContent = errorText;
          errorEl.hidden = false;
        }
        resetButton(button);
      }
    }

    function restoreButtonLabel(button) {
      if (!button) return;
      button.textContent = button.dataset.defaultText || button.textContent;
    }

    function resetButton(button) {
      if (!button) return;
      button.disabled = false;
      restoreButtonLabel(button);
    }

    async function onResend() {
      const message = root.querySelector("[data-resend-message]");
      const note = root.querySelector("[data-resent-note]");
      if (note) note.hidden = true;
      if (message) {
        message.hidden = true;
        message.textContent = "";
      }
      resendButton.disabled = true;
      resendButton.textContent =
        resendButton.dataset.loadingText || resendButton.textContent;

      try {
        const res = await fetch(gatedContentApiUrl, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ intent: "resend" }),
        });
        const result = await res.json();
        if (!result.success) {
          if (message) {
            message.textContent = result.message || errorText;
            message.hidden = false;
          }
          resetButton(resendButton);
          return;
        }

        // Label back to normal, but the button stays disabled: a second click
        // would delete and recreate the recipient again.
        restoreButtonLabel(resendButton);

        toggleResentNote(result.activationMailResent === true);
        if (result.resendThrottled && message) {
          message.textContent = throttledMessage(result.retryAfter);
          message.hidden = message.textContent === "";
        }
      } catch (err) {
        console.error("[newsletter-signup] resend error:", err);
        if (message) {
          message.textContent = errorText;
          message.hidden = false;
        }
        resetButton(resendButton);
      }
    }

    /*
     * Names the moment a new mail can be requested. Without it the customer only
     * learns that it did not work, not when to come back.
     */
    function throttledMessage(retryAfter) {
      const message = root.querySelector("[data-resend-message]");
      const base = message?.dataset.throttledText || "";
      if (!retryAfter) return base;
      const readyAt = new Date(retryAfter);
      if (Number.isNaN(readyAt.getTime())) return base;
      const time = readyAt.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
      });
      const suffix = message?.dataset.throttledTimeSuffix || "";
      return suffix ? `${base} ${suffix.replace("{time}", time)}` : base;
    }

    function toggleResentNote(wasResent) {
      const note = root.querySelector("[data-resent-note]");
      if (note) note.hidden = !wasResent;
    }

    function setState(state) {
      root.setAttribute("data-newsletter-status-active", state);
    }

    function showError(message) {
      const errorEl = root.querySelector("[data-error-text]");
      if (errorEl && message) errorEl.textContent = message;
      setState("error");
    }
  }
})();
